import React, { useState, useEffect } from 'react';
import { certificatesApi, tenantsApi, Certificate } from '../services/api';

const CertificatesManager: React.FC = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [uploadData, setUploadData] = useState({
    tenantId: '',
    password: '',
    file: null as File | null,
  });

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await certificatesApi.getAll();
      setCertificates(response.data.certificates || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch certificates');
      console.error('Certificates fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadData({ ...uploadData, file });
    }
  };

  const uploadCertificate = async () => {
    if (!uploadData.file || !uploadData.tenantId || !uploadData.password) {
      setError('Please fill all required fields');
      return;
    }

    try {
      await certificatesApi.upload(uploadData.tenantId, uploadData.file, uploadData.password);
      setUploadData({ tenantId: '', password: '', file: null });
      setShowUploadModal(false);
      fetchCertificates(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to upload certificate');
    }
  };

  const handleViewCertificate = async (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setShowViewModal(true);

    // Fetch tenant name if not already present
    if (!certificate.tenant_name && certificate.tenant_id) {
      try {
        const response = await tenantsApi.getById(certificate.tenant_id);
        if (response.data) {
          setSelectedCertificate({
            ...certificate,
            tenant_name: response.data.company_name
          });
        }
      } catch (err) {
        console.error('Failed to fetch tenant name:', err);
      }
    }
  };

  const handleDownloadCertificate = async (certificate: Certificate) => {
    try {
      const response = await certificatesApi.download(certificate.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', certificate.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download certificate');
    }
  };

  const handleDeleteCertificate = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setShowDeleteModal(true);
  };

  const confirmDeleteCertificate = async () => {
    if (!selectedCertificate) return;

    try {
      await certificatesApi.delete(selectedCertificate.id);
      setShowDeleteModal(false);
      setSelectedCertificate(null);
      fetchCertificates(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete certificate');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'valid':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'expiring':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isExpiringSoon = (validTo: string) => {
    if (!validTo) return false;
    const expiryDate = new Date(validTo);
    if (isNaN(expiryDate.getTime())) return false;
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (validTo: string) => {
    if (!validTo) return false;
    const expiryDate = new Date(validTo);
    if (isNaN(expiryDate.getTime())) return false;
    return expiryDate < new Date();
  };

  const getDaysUntilExpiry = (validTo: string) => {
    if (!validTo) return null;
    const expiryDate = new Date(validTo);
    if (isNaN(expiryDate.getTime())) return null;
    const now = new Date();
    return Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading certificates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Certificates Management</h2>
          <p className="mt-1 text-sm text-gray-600">Manage digital certificates for SUNAT integration</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          📤 Upload Certificate
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchCertificates}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">🔐</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Certificates</p>
              <p className="text-2xl font-bold text-gray-900">{certificates.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">✅</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Valid</p>
              <p className="text-2xl font-bold text-green-600">
                {certificates.filter(cert => !isExpired(cert.valid_to) && !isExpiringSoon(cert.valid_to)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">⚠️</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-600">
                {certificates.filter(cert => isExpiringSoon(cert.valid_to)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">❌</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-red-600">
                {certificates.filter(cert => isExpired(cert.valid_to)).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Certificates</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certificate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issuer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Until
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {certificates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">🔐</div>
                    <p>No certificates found</p>
                    <p className="text-sm">Upload your first certificate to get started</p>
                  </td>
                </tr>
              ) : (
                certificates.map((cert) => {
                  const daysUntilExpiry = getDaysUntilExpiry(cert.valid_to);
                  const expired = isExpired(cert.valid_to);
                  const expiringSoon = isExpiringSoon(cert.valid_to);

                  return (
                    <tr key={cert.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{cert.filename}</p>
                          <p className="text-sm text-gray-500">Serial: {cert.serial_number}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={cert.subject}>
                          {cert.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={cert.issuer}>
                          {cert.issuer}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <p>{formatDate(cert.valid_to)}</p>
                          {!expired && cert.valid_to && daysUntilExpiry !== null && (
                            <p className={`text-xs ${expiringSoon ? 'text-yellow-600' : 'text-gray-500'}`}>
                              {daysUntilExpiry} days remaining
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          !cert.valid_to ? 'bg-gray-100 text-gray-800' :
                          expired ? 'bg-red-100 text-red-800' :
                          expiringSoon ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {!cert.valid_to ? 'Invalid' : expired ? 'Expired' : expiringSoon ? 'Expiring Soon' : 'Valid'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewCertificate(cert)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadCertificate(cert)}
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => handleDeleteCertificate(cert)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Certificate Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Certificate</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tenant ID (RUC)</label>
                  <input
                    type="text"
                    value={uploadData.tenantId}
                    onChange={(e) => setUploadData({ ...uploadData, tenantId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter RUC or tenant ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Certificate File (.p12)</label>
                  <input
                    type="file"
                    accept=".p12,.pfx"
                    onChange={handleFileChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Select a .p12 or .pfx certificate file</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={uploadData.password}
                    onChange={(e) => setUploadData({ ...uploadData, password: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Certificate password"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 space-x-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadCertificate}
                  disabled={!uploadData.file || !uploadData.tenantId || !uploadData.password}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Upload Certificate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Certificate Modal */}
      {showViewModal && selectedCertificate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Certificate Details</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Filename</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedCertificate.filename}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant</label>
                    <p className="mt-1 text-sm text-gray-900 font-medium">
                      {selectedCertificate.tenant_name || 'Loading...'}
                    </p>
                    <p className="text-xs text-gray-500">{selectedCertificate.tenant_id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Serial Number</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono">{selectedCertificate.serial_number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      !selectedCertificate.valid_to ? 'bg-gray-100 text-gray-800' :
                      isExpired(selectedCertificate.valid_to) ? 'bg-red-100 text-red-800' :
                      isExpiringSoon(selectedCertificate.valid_to) ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {!selectedCertificate.valid_to ? 'Invalid' :
                       isExpired(selectedCertificate.valid_to) ? 'Expired' :
                       isExpiringSoon(selectedCertificate.valid_to) ? 'Expiring Soon' : 'Valid'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <p className="mt-1 text-sm text-gray-900 break-all">{selectedCertificate.subject}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Issuer</label>
                  <p className="mt-1 text-sm text-gray-900 break-all">{selectedCertificate.issuer}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Valid From</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedCertificate.valid_from)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Valid To</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedCertificate.valid_to)}</p>
                    {selectedCertificate.valid_to && !isExpired(selectedCertificate.valid_to) && getDaysUntilExpiry(selectedCertificate.valid_to) !== null && (
                      <p className={`text-xs ${
                        isExpiringSoon(selectedCertificate.valid_to) ? 'text-yellow-600' : 'text-gray-500'
                      }`}>
                        {getDaysUntilExpiry(selectedCertificate.valid_to)} days remaining
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 space-x-3">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDownloadCertificate(selectedCertificate)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Certificate Modal */}
      {showDeleteModal && selectedCertificate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Certificate</h3>

              <div className="text-center">
                <div className="text-4xl mb-4">⚠️</div>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this certificate?
                </p>
                <div className="bg-gray-50 p-3 rounded mb-4">
                  <p className="text-sm font-medium text-gray-900">{selectedCertificate.filename}</p>
                  <p className="text-xs text-gray-500">Serial: {selectedCertificate.serial_number}</p>
                </div>
                <p className="text-xs text-red-600">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex items-center justify-end pt-6 space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCertificate}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Certificate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificatesManager;